/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business ファネル詳細ページの dimension タブで使うピボット変換。ファネル API レスポンスの (step, dimensionValue, uniqueUsers) を dimensionValue × step ユーザー数の表形式に並べ替える。
 */

interface PivotData {
  steps: string[];
  values: {
    dimensionValue: string;
    stepUsers: Record<string, number>;
  }[];
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business dimensions API の出力をステップ × dimensionValue のピボットに変換する。stepOrder が指定されていればファネル定義順を尊重し、定義外のステップは末尾に追加する。dimensionValue は最初のステップのユーザー数で降順ソートし、TOP 20 に絞る。
 * @graph-connects none
 */
export function buildPivotData(
  data: { stepName: string; dimensionValue: string; uniqueUsers: number }[],
  stepOrder: string[] = [],
): PivotData {
  const stepSet = new Set<string>();
  const valueMap = new Map<string, Record<string, number>>();

  for (const row of data) {
    stepSet.add(row.stepName);
    if (!valueMap.has(row.dimensionValue)) {
      valueMap.set(row.dimensionValue, {});
    }
    const entry = valueMap.get(row.dimensionValue);
    if (entry) {
      entry[row.stepName] = (entry[row.stepName] ?? 0) + row.uniqueUsers;
    }
  }

  // ファネル定義順でソート。定義にないステップは末尾に追加
  const allSteps = Array.from(stepSet);
  const steps =
    stepOrder.length > 0
      ? [
          ...stepOrder.filter((s) => stepSet.has(s)),
          ...allSteps.filter((s) => !stepOrder.includes(s)),
        ]
      : allSteps;

  // 最初のステップのユーザー数で降順ソート
  const values = Array.from(valueMap.entries())
    .map(([dimensionValue, stepUsers]) => ({ dimensionValue, stepUsers }))
    .sort((a, b) => {
      const aFirst = a.stepUsers[steps[0] ?? ''] ?? 0;
      const bFirst = b.stepUsers[steps[0] ?? ''] ?? 0;
      return bFirst - aFirst;
    })
    .slice(0, 20);

  return { steps, values };
}
